// frontend\src\components\analytics\InventorySearchFilter.tsx
import React, { useRef, useState, useEffect, Dispatch, SetStateAction } from 'react';
import api from '../../utils/api';
import { SearchSuggestion } from '../../types';

interface InventorySearchFilterProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
}

const InventorySearchFilter: React.FC<InventorySearchFilterProps> = ({
  search,
  setSearch,
}) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState<number>(-1);

  useEffect(() => {
    const fetchSuggestions = async (query: string) => {
      if (!query.trim()) {
        setSuggestions([]);
        setShowSuggestions(false);
        setFocusedSuggestionIndex(-1);
        return;
      }
      setSuggestionsLoading(true);
      try {
        const response = await api.get('/search', { params: { query, include_variations: 'true' } });
        // Filter to only products
        const productSuggestions = response.data.suggestions.filter(
          (suggestion: SearchSuggestion) => suggestion.type === 'product'
        );
        setSuggestions(productSuggestions || []);
        setShowSuggestions(true);
      } catch (error: any) {
        console.error('Search suggestions error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setSuggestionsLoading(false);
      }
    };

    const debounce = setTimeout(() => {
      if (searchQuery) fetchSuggestions(searchQuery);
    }, 200);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setFocusedSuggestionIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedItems(
      search
        .split(',')
        .filter((v) => v)
        .map((value) => ({
          type: 'product',
          value,
          label: value,
        }))
    );
  }, [search]);

  const handleSelectItem = (item: SearchSuggestion) => {
    if (!selectedItems.find((i) => i.value === item.value)) {
      const newSelectedItems = [...selectedItems, item];
      setSelectedItems(newSelectedItems);
      setSearch(newSelectedItems.map((i) => i.value).join(','));
      setSearchQuery('');
      setShowSuggestions(false);
      setFocusedSuggestionIndex(-1);
      searchInputRef.current?.focus();
    }
  };

  const handleRemoveItem = (value: string) => {
    const newSelectedItems = selectedItems.filter((item) => item.value !== value);
    setSelectedItems(newSelectedItems);
    setSearch(newSelectedItems.map((i) => i.value).join(','));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && focusedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSelectItem(suggestions[focusedSuggestionIndex]);
    } else if (e.key === 'Backspace' && !searchQuery && selectedItems.length > 0) {
      e.preventDefault();
      handleRemoveItem(selectedItems[selectedItems.length - 1].value);
    }
  };

  return (
    <div ref={searchContainerRef} className="analytics-search-box">
      {selectedItems.map((item) => (
        <span key={item.value} className={`analytics-tag ${item.type}`}>
          {item.label} ({item.type})
          <button className="analytics-tag-remove" onClick={() => handleRemoveItem(item.value)}>
            ×
          </button>
        </span>
      ))}
      <input
        ref={searchInputRef}
        type="text"
        placeholder={
          selectedItems.length === 0 ? 'Filter by SKU or Product Name' : ''
        }
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        className="analytics-search-input"
        disabled={suggestionsLoading}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="analytics-dialog">
          <ul className="analytics-list">
            {suggestions.map((item, index) => (
              <li
                key={`${item.type}-${item.value}`}
                className={`analytics-item ${item.type} ${index === focusedSuggestionIndex ? 'focused' : ''}`}
                onClick={() => handleSelectItem(item)}
              >
                {item.label} ({item.type})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InventorySearchFilter;